// React Imports
import React, { useEffect, useRef, useState } from "react";
import { useId, useBoolean } from "@fluentui/react-hooks";

// Internationalization Import
import i18n from "i18next";

// Internal Imports
import { fetchWrapper } from "../../helpers/fetchwrapper";
import { IAddMediathekDocumentDto } from "../../Models/IAddMediathekDocument";
import { baseUrl } from "../../helpers/constants";
import { IDokumentgruppe } from "../../Models/IDokumentGruppe";
import { ISprache } from "../../Models/ISprache";
import { IArtikel } from "../../Models/IArtikel";
import { Label } from "reactstrap";
import useWindowSize from "../../helpers/useWindowSize";
import {
  Checkbox,
  TagPicker,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  MessageBar,
  Option,
  Text,
  Textarea,
  Combobox,
  Tooltip,
  ProgressBar,
} from "@fluentui/react-components";
import {
  ArrowUploadRegular,
  CalendarRegular,
  DismissRegular,
  InfoRegular,
} from "@fluentui/react-icons";
import { StackShim } from "@fluentui/react-migration-v8-v9";
import { ILuebOption } from "../../Models/ILuebOption";
import { DatePicker } from "@fluentui/react-datepicker-compat";

// Props
interface IAddMediathekDocumentDialogProps {
  academyDocumentFolderGuid?: string;
  isDialogOpen: boolean;
  onDismiss: any;
}

// Function
function AddMediathekDocumentDialog(props: IAddMediathekDocumentDialogProps) {
  const newDoc = {
    artikelId: "",
    description: "",
    spracheGuid: "",
    docGroupGuid: "",
    publicForAll: false,
    publicForLdUsers: false,
    isAcademyDocument: props.academyDocumentFolderGuid ? true : false,
    validUntil: undefined,
  };

  /* const addMediathekDocumentDialogProps = {
    type: DialogType.largeHeader,
    title: i18n.t<string>("uploadFile"),
    subText: i18n.t<string>("uploadFileText"),
  }; */

  const [showError, setShowError] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);

  const inputFile = useRef<any>(null);

  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);
  const [dokumentgruppen, setDokumentgruppen] = useState<ILuebOption[]>([]);
  const [sprachen, setSprachen] = useState<ILuebOption[]>([]);

  const [
    publicForLdUsersBubbleVisible,
    { toggle: togglePublicForLdUsersBubbleVisible },
  ] = useBoolean(false);

  const [
    publicForAllBubbleVisible,
    { toggle: togglePublicForAllBubbleVisible },
  ] = useBoolean(false);

  const [
    academyDocumentBubbleVisible,
    { toggle: toggleAcademyDocumentBubbleVisible },
  ] = useBoolean(false);

  const windowSize = useWindowSize();

  const [allArtikel, setAllArtikel] = useState<IArtikel[]>([]);

  const [documentProps, setDocumentProps] = useState<
    IAddMediathekDocumentDto | undefined
  >(newDoc);
  const handleDocumentPropsChange = (name: string, val: any) => {
    setDocumentProps((prevState: any) => ({
      ...prevState,
      [name]: val,
    }));
  };

  const onSelectedFile = (event: any) => {
    if (event.target.files.length == 1) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const uploadFile = () => {
    setShowError(false);
    setUploading(true);
    if (selectedFile != undefined && documentProps) {
      var data = new FormData();
      data.append("file", selectedFile);
      fetchWrapper
        .sendFilesWithAuth(
          `${baseUrl}/api/Mediathek/UploadFile?artikelId=${
            documentProps.artikelId
          }&description=${documentProps.description}&spracheGuid=${
            documentProps.spracheGuid
          }&docGroupGuid=${
            documentProps.docGroupGuid
          }&academyDocumentFolderGuid=${
            props.academyDocumentFolderGuid
              ? props.academyDocumentFolderGuid
              : ""
          }&publicForAll=${documentProps.publicForAll}&publicForLdUsers=${
            documentProps.publicForLdUsers
          }&isAcademyDocument=${documentProps.isAcademyDocument}&validUntil=${
            documentProps.validUntil != undefined
              ? documentProps.validUntil.toISOString()
              : ""
          }`,
          data,
        )
        .then((result) => {
          setUploading(false);
          props.onDismiss();
        })
        .catch((error: any) => {
          setShowError(true);
          setUploading(false);
        });
    }
  };
  useEffect(() => {
    getArtikel();
  }, []);
  const getArtikel = (/* artikelId: string */) => {
    return fetchWrapper
      .get(`${baseUrl}/api/mediathek/GetArtikel`)
      .then((data: IArtikel[]) => {
        const ids: any[] = [];
        data.forEach((element: IArtikel) => {
          ids.push({
            key: element.artikelId ?? "",
            name:
              element.artikelId +
              " " +
              (i18n.language.includes("de")
                ? element.benennungDE
                : element.benennungEN),
          });
        });

        const newArtikel = [...allArtikel];

        data.forEach((y) => {
          if (!allArtikel.some((x) => y.artikelId == x.artikelId)) {
            newArtikel.push(y);
          }
        });

        setAllArtikel(newArtikel);

        return ids;
      })
      .catch(() => {
        const emptyList: any[] = [];
        return emptyList;
      });
  };

  const getDokumentGruppen = () => {
    fetchWrapper
      .getWithAuth(`${baseUrl}/api/mediathek/getDokumentGruppen`)
      .then((data: IDokumentgruppe[]) => {
        let options: ILuebOption[] = [];
        data.forEach((element: IDokumentgruppe) => {
          let option: ILuebOption = {
            key: element.dokumentgruppeGuid,
            text: i18n.language.includes("de")
              ? element.nameDE
              : element.nameEN,
          };
          options.push(option);
        });

        options.sort((a, b) => (a.text < b.text ? -1 : 1));

        setDokumentgruppen(options);
      });
  };

  const getSprachen = () => {
    fetchWrapper
      .get(`${baseUrl}/api/mediathek/getSprachen`)
      .then((data: ISprache[]) => {
        let options: ILuebOption[] = [];
        data.forEach((element: ISprache) => {
          let option: ILuebOption = {
            key: element.spracheGuid,
            text: i18n.language.includes("de")
              ? element.nameDE
              : element.nameEN,
          };
          options.push(option);
        });

        options.sort((a, b) => (a.text < b.text ? -1 : 1));

        setSprachen(options);
      });
  };

  useEffect(() => {
    if (props.isDialogOpen) {
      getSprachen();
      getDokumentGruppen();
    } else {
      setDocumentProps(newDoc);
    }
  }, [props.isDialogOpen]);

  return (
    <div>
      <Dialog open={props.isDialogOpen}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{i18n.t<string>("uploadFile")}</DialogTitle>
            <DialogContent>
              <StackShim tokens={{ childrenGap: 10 }}>
                <Text>{i18n.t<string>("uploadFileText")}</Text>
                <Field
                  label={i18n.t<string>("file")}
                  required
                  style={{ width: "" }}
                >
                  <Input
                    appearance="filled-darker"
                    value={selectedFile?.name ?? ""}
                    readOnly
                    contentBefore={
                      <Button
                        appearance="transparent"
                        disabled={uploading}
                        icon={<ArrowUploadRegular />}
                        onClick={() =>
                          inputFile ? inputFile.current.click() : undefined
                        }
                      />
                    }
                  />
                  <input
                    disabled={!props.isDialogOpen || uploading}
                    type="file"
                    id="file"
                    accept="video/mp4,.pdf,.zip,.stp"
                    ref={inputFile}
                    style={{ display: "none" }}
                    onChange={onSelectedFile}
                  />
                </Field>

                <Field required label={i18n.t<string>("docGroup")}>
                  <Dropdown
                    disabled={uploading}
                    selectedOptions={[documentProps?.docGroupGuid ?? ""]}
                    value={
                      dokumentgruppen.find(
                        (x) => x.key == documentProps?.docGroupGuid,
                      )?.text ?? ""
                    }
                    onOptionSelect={(event, data) => {
                      if (data) {
                        handleDocumentPropsChange(
                          "docGroupGuid",
                          data.optionValue,
                        );
                      } else {
                        handleDocumentPropsChange("docGroupGuid", "");
                      }
                    }}
                  >
                    {dokumentgruppen.map((item) => {
                      if (typeof item.key == "string") {
                        return <Option value={item.key}>{item.text}</Option>;
                      }
                    })}
                  </Dropdown>
                </Field>
                <Field required label={i18n.t<string>("language")}>
                  <Dropdown
                    disabled={uploading}
                    selectedOptions={[documentProps?.spracheGuid ?? ""]}
                    value={
                      sprachen.find((x) => x.key == documentProps?.spracheGuid)
                        ?.text ?? ""
                    }
                    onOptionSelect={(event, data) => {
                      if (data) {
                        handleDocumentPropsChange(
                          "spracheGuid",
                          data.optionValue,
                        );
                      } else {
                        handleDocumentPropsChange("spracheGuid", "");
                      }
                    }}
                  >
                    {sprachen.map((item) => {
                      if (typeof item.key == "string") {
                        return <Option value={item.key}>{item.text}</Option>;
                      }
                    })}
                  </Dropdown>
                </Field>
                <Field required label={i18n.t<string>("description")}>
                  <Textarea
                    resize="vertical"
                    disabled={uploading}
                    value={documentProps?.description}
                    onChange={(ev, val) => {
                      handleDocumentPropsChange(
                        "description",
                        val ? val.value : "",
                      );
                    }}
                  />
                </Field>

                <Field label={i18n.t<string>("artikelId")}>
                  <Combobox
                    onOptionSelect={(ev, data) => {
                      if (data.selectedOptions?.length == 1) {
                        handleDocumentPropsChange(
                          "artikelId",
                          data.selectedOptions[0],
                        );
                      } else {
                        handleDocumentPropsChange("artikelId", "");
                      }
                    }}
                  >
                    {allArtikel.map((artikel) => {
                      if (typeof artikel.artikelId == "string") {
                        return (
                          <Option value={artikel.artikelId}>
                            {artikel.artikelId +
                              " " +
                              (i18n.language.includes("de")
                                ? artikel.benennungDE
                                : artikel.benennungEN)}
                          </Option>
                        );
                      }
                    })}
                  </Combobox>
                </Field>
                {props.academyDocumentFolderGuid && (
                  <Field label={"Verfügbar bis"}>
                    <DatePicker
                      onSelectDate={(date) => {
                        handleDocumentPropsChange("validUntil", date);
                      }}
                      value={documentProps?.validUntil ?? undefined}
                      formatDate={(date) => {
                        return date ? date.toLocaleDateString() : "";
                      }}
                      contentAfter={
                        <>
                          {documentProps?.validUntil != undefined ? (
                            <Button
                              appearance="transparent"
                              icon={<DismissRegular />}
                              onClick={() => {
                                handleDocumentPropsChange(
                                  "validUntil",
                                  undefined,
                                );
                              }}
                            />
                          ) : (
                            <CalendarRegular />
                          )}
                        </>
                      }
                    />
                  </Field>
                )}

                {!props.academyDocumentFolderGuid && (
                  <StackShim horizontal verticalAlign={"center"}>
                    <Checkbox
                      disabled={uploading}
                      checked={documentProps?.publicForAll}
                      label={i18n.t<string>("publicForAll")}
                      onChange={(ev, checked) => {
                        handleDocumentPropsChange(
                          "publicForAll",
                          checked ? checked.checked : false,
                        );
                      }}
                    />

                    {windowSize.height > 500 && windowSize.width > 600 && (
                      <>
                        <Tooltip
                          content={i18n.t<string>("publicForAllHint")}
                          relationship="label"
                        >
                          <InfoRegular />
                        </Tooltip>
                      </>
                    )}
                  </StackShim>
                )}

                {!props.academyDocumentFolderGuid && (
                  <StackShim horizontal verticalAlign={"center"}>
                    <Checkbox
                      disabled={documentProps?.publicForAll || uploading}
                      checked={
                        documentProps?.publicForAll
                          ? documentProps?.publicForAll
                          : documentProps?.publicForLdUsers
                      }
                      label={i18n.t<string>("publicForLdUsers")}
                      onChange={(ev, checked) => {
                        handleDocumentPropsChange(
                          "publicForLdUsers",
                          checked ? checked.checked : false,
                        );
                      }}
                    />

                    {windowSize.height > 500 && windowSize.width > 600 && (
                      <>
                        <Tooltip
                          content={i18n.t<string>("publicForLdUsersHint")}
                          relationship="label"
                        >
                          <InfoRegular />
                        </Tooltip>
                      </>
                    )}
                  </StackShim>
                )}

                {!props.academyDocumentFolderGuid && (
                  <StackShim horizontal verticalAlign={"center"}>
                    <Checkbox
                      disabled={
                        (props.academyDocumentFolderGuid != undefined &&
                          props.academyDocumentFolderGuid != "") ||
                        uploading
                      }
                      checked={documentProps?.isAcademyDocument}
                      label={i18n.t<string>("isAcademyDocument")}
                      onChange={(ev, checked) => {
                        handleDocumentPropsChange(
                          "isAcademyDocument",
                          checked ? checked.checked : false,
                        );
                      }}
                    />
                    {/* {windowSize.height > 500 && windowSize.width > 600 && (
                      <FontIcon
                        id="academyDocumentBubbleVisibleId"
                        style={{ marginLeft: 5, color: "#ff6600" }}
                        iconName="Info"
                        onMouseOver={toggleAcademyDocumentBubbleVisible}
                        onMouseOut={toggleAcademyDocumentBubbleVisible}
                      />
                    )}*/}

                    {windowSize.height > 500 && windowSize.width > 600 && (
                      <>
                        <Tooltip
                          content={i18n.t<string>("academyDocumentHint")}
                          relationship="label"
                        >
                          <InfoRegular />
                        </Tooltip>
                      </>
                    )}
                  </StackShim>
                )}

                {showError && (
                  <MessageBar intent={"error"}>
                    {i18n.t<string>("somethingWentWrong")}
                  </MessageBar>
                )}

                {uploading && <ProgressBar />}
              </StackShim>
            </DialogContent>
            <DialogActions>
              <Button
                icon={<ArrowUploadRegular />}
                appearance="primary"
                onClick={() => {
                  uploadFile();
                }}
                disabled={
                  uploading ||
                  !selectedFile ||
                  !documentProps?.spracheGuid ||
                  !documentProps?.description ||
                  !documentProps?.docGroupGuid
                }
              >
                {i18n.t<string>("uploadFile")}
              </Button>
              <Button onClick={() => props.onDismiss()}>
                {i18n.t<string>("cancel")}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}

// Export Function
export default AddMediathekDocumentDialog;
